import apiClient from '../api/base/apiClient'

/**
 * 스크롤이 막히는 순간의 상태를 서버에 남긴다.
 *
 * 왜 필요한가
 * -----------
 * 리뷰 작성 화면에서 사진을 올린 직후 화면이 스크롤되지 않는 일이 아주 가끔 있다.
 * 안드로이드 실기기에서만 나타나고 재현이 안 돼 원인을 특정하지 못했다.
 * 다음에 발생하면 추측 없이 잡을 수 있도록, 의심 조건이 성립하는 순간의
 * 화면 상태를 기록해 둔다.
 *
 * 사용자에게는 아무것도 보이지 않는다. 화면 변화도, 알림도 없다.
 * 실패해도 조용히 넘어가며 앱 동작에 영향을 주지 않는다.
 */

/** 같은 증상을 반복해서 보내지 않도록 화면당 한 번만 */
const reported = new Set<string>()

/**
 * 앱을 켠 뒤 보낼 수 있는 최대 건수.
 *
 * 사진 편집 화면을 오갈 때마다 화면이 다시 마운트되면서 보고가 초기화돼,
 * 사진을 여러 장 붙이면 요청이 몰려 서버 제한(분당 5회)에 걸렸다.
 * 진단은 몇 건만 있어도 원인 파악에 충분하므로 상한을 둔다.
 */
const MAX_REPORTS_PER_SESSION = 3
let sentCount = 0

type ScrollBlocker = {
  reason: string
  tag: string
  className: string
  clientHeight: number
  scrollHeight: number
  // 가로도 함께 남긴다. 세로 값만 있으면 걸린 요소가 '가로 줄'인지
  // '본문'인지 로그만 보고는 끝내 구분할 수 없었다.
  clientWidth: number
  scrollWidth: number
  overflowY: string
  touchAction: string
}

/** 요소를 짧게 식별할 수 있는 문자열로 */
function describe(el: Element): Pick<ScrollBlocker, 'tag' | 'className'> {
  return {
    tag: el.tagName.toLowerCase(),
    className: (el.className || '').toString().slice(0, 120),
  }
}

/**
 * 세로 스크롤을 가로챌 수 있는 요소를 찾는다.
 *
 * 가로 스크롤만 의도한 영역(사진 줄 등)은 CSS 규칙상 세로도 auto가 되어,
 * 세로로 조금이라도 넘치면 그 안에서 스크롤이 소비된다. 사진 한 줄의
 * 높이가 내용보다 2px만 모자라도 손가락이 본문 대신 그 줄을 움직인다.
 */
function findBlockers(root: ParentNode): ScrollBlocker[] {
  const found: ScrollBlocker[] = []

  root.querySelectorAll('*').forEach((el) => {
    const style = window.getComputedStyle(el)
    const scrollsVertically =
      (style.overflowY === 'auto' || style.overflowY === 'scroll') &&
      el.scrollHeight > el.clientHeight + 1

    if (!scrollsVertically) {
      return
    }

    /*
     * 의도한 세로 스크롤 영역(페이지 본문)은 제외 — 가로 스크롤을 겸한 곳만 문제다.
     *
     * 예전에는 overflow-x가 auto/scroll인지로 걸렀는데, 그 검사는 아무것도
     * 거르지 못했다. CSS는 overflow-y에 auto를 주면 overflow-x도 auto로
     * 계산하므로(둘 중 하나가 visible이 아니면 나머지가 auto가 된다),
     * 세로 스크롤 영역은 예외 없이 이 검사를 통과했다.
     *
     * 그래서 본문(app-container flex-1 overflow-y-auto)만 115번 신고됐고
     * 정작 찾으려던 가로 줄은 한 번도 구분되지 않았다. 실측:
     *
     *   본문      overflow-x 안 적음 → 계산값 auto   scrollW 500 = clientW 500
     *   사진 줄   overflow-x 안 적음 → 계산값 auto   scrollW 760 > clientW 500
     *
     * overflow-x로는 둘을 나눌 수 없다. 가로로 실제 넘치는지로 나눈다.
     */
    if (el.scrollWidth <= el.clientWidth + 1) {
      return
    }

    found.push({
      reason: 'horizontal_row_scrolls_vertically',
      ...describe(el),
      clientHeight: el.clientHeight,
      scrollHeight: el.scrollHeight,
      clientWidth: el.clientWidth,
      scrollWidth: el.scrollWidth,
      overflowY: style.overflowY,
      touchAction: style.touchAction,
    })
  })

  return found
}

/** body에 스크롤 잠금이 남아 있는지 */
function bodyLockState() {
  const body = window.getComputedStyle(document.body)
  const locked = body.overflow === 'hidden' || body.position === 'fixed'
  return locked
    ? { overflow: body.overflow, position: body.position }
    : null
}

/**
 * 스크롤이 막힐 만한 상태인지 살펴보고, 그렇다면 서버에 한 번 보고한다.
 *
 * @param context 어느 화면·어느 시점인지 (예: 'store_review:photo_added')
 * @param scrollerSelector 원래 스크롤돼야 하는 컨테이너
 */
export function reportIfScrollBlocked(
  context: string,
  scrollerSelector: string
): void {
  try {
    const scroller = document.querySelector(scrollerSelector)
    const blockers = findBlockers(document.body)
    const bodyLock = bodyLockState()

    // 이상 징후가 없으면 아무것도 하지 않는다
    if (blockers.length === 0 && !bodyLock) {
      return
    }

    if (sentCount >= MAX_REPORTS_PER_SESSION) {
      return
    }

    const key = `${context}:${blockers.map((b) => b.className).join('|')}:${bodyLock ? 'lock' : ''}`
    if (reported.has(key)) {
      return
    }
    reported.add(key)
    sentCount++

    void apiClient
      .post(
        '/diagnostics/scroll-block',
        {
          context,
          url: window.location.pathname,
          blockers: blockers.slice(0, 5),
          body_lock: bodyLock,
          scroller: scroller
            ? {
                clientHeight: scroller.clientHeight,
                scrollHeight: scroller.scrollHeight,
                scrollTop: scroller.scrollTop,
              }
            : null,
          viewport: {
            innerHeight: window.innerHeight,
            innerWidth: window.innerWidth,
            dpr: window.devicePixelRatio,
          },
        },
        // 진단 요청의 실패를 사용자에게 보여주면 안 된다.
        // 이 옵션이 없으면 공용 에러 처리기가 토스트를 띄운다. 실제로 호출이 몰려
        // 429가 나면서 사진 첨부 중에 "Too Many Attempts."가 사용자에게 떴다.
        { skipErrorHandler: true } as any
      )
      .catch(() => {
        // 진단 실패가 사용자 경험을 건드리면 안 된다
      })
  } catch {
    // 진단 코드 자체가 앱을 멈추게 해서는 안 된다
  }
}

/** 화면을 벗어나면 다시 보고할 수 있도록 초기화 */
export function resetScrollDiagnostics(): void {
  reported.clear()
}
